import React, { useState, useEffect } from "react";
import {
  Button,
  InputAdornment,
  TablePagination,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Box,
  TableContainer,
  TableCell,
  Table,
  TableHead,
  Paper,
  TableRow,
  TableBody,
  Card,
  CardHeader,
  Avatar,
  Grid,
  TextField,
  IconButton,
} from "@mui/material";
import { UrlApi } from "../../../../services/apirest";
import moment from "moment";
import CircularProgress from "@mui/material/CircularProgress";
import SearchIcon from "@mui/icons-material/Search";
import Title from "../../../Title";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

export default function Saldos(props) {
  const [saldos, setSaldos] = useState([]);
  const [filteredSaldos, setFilteredSaldos] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalRegistros, setTotalRegistros] = useState(0); // Total de registros
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(0); // Resetear a la primera página
  };

  const handleSearch = (event) => {
    const term = event.target.value;
    setSearchTerm(term);
    setPage(0); // Resetear a la primera página al buscar

    if (term.trim() === "") {
      setFilteredSaldos(saldos); // Mostrar todos los datos si no hay término de búsqueda
    } else {
      const filtered = saldos.filter(
        (row) =>
          (row["CCD-COMPROBANTE"]?.toLowerCase() || "").includes(
            term.toLowerCase()
          ) ||
          (row["CCD-RENDICION"]?.toLowerCase() || "").includes(
            term.toLowerCase()
          )
      );
      setFilteredSaldos(filtered);
    }
  };

  useEffect(() => {
    if (!props.dataCliente?.sisven_id || !props.dataCliente?.id_empresa) return;

    const fetchData = async () => {
      setLoading(true);
      const endpoint =
        props.dataCliente.id_empresa === 1
          ? `${UrlApi}/apicobol/registros_hipergas`
          : `${UrlApi}/apicobol/registros_autogas`;

      const inicio = page * rowsPerPage + 1;
      // console.log("Solicitud al backend:", { inicio, registros: rowsPerPage });

      try {
        const response = await fetch(
          `${endpoint}?parametro=${props.dataCliente.sisven_id}&inicio=${inicio}&registros=${rowsPerPage}`,
          {
            headers: {
              Authorization: localStorage.getItem("token"),
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Error HTTP: ${response.status}`);
        }

        const data = await response.json();
        // console.log("Respuesta del backend:", data);

        setSaldos(data.registros || []);
        // Aplicar el filtro de búsqueda solo si hay un término de búsqueda
        if (searchTerm.trim() === "") {
          setFilteredSaldos(data.registros || []);
        } else {
          const filtered = (data.registros || []).filter(
            (row) =>
              (row["CCD-COMPROBANTE"]?.toLowerCase() || "").includes(
                searchTerm.toLowerCase()
              ) ||
              (row["CCD-RENDICION"]?.toLowerCase() || "").includes(
                searchTerm.toLowerCase()
              )
          );
          setFilteredSaldos(filtered);
        }
        setTotal(data.saldo || 0);
        setTotalRegistros(Number(data.cantidad_registros) || 0); // Usar cantidad_registros
      } catch (error) {
        console.error("Error fetching data:", error);
        setSaldos([]);
        setFilteredSaldos([]);
        setTotal(0);
        setTotalRegistros(0);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    props.dataCliente?.sisven_id,
    props.dataCliente?.id_empresa,
    page,
    rowsPerPage,
  ]);

  // Actualizar filteredSaldos cuando cambie searchTerm
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredSaldos(saldos);
    } else {
      const filtered = saldos.filter(
        (row) =>
          (row["CCD-COMPROBANTE"]?.toLowerCase() || "").includes(
            searchTerm.toLowerCase()
          ) ||
          (row["CCD-RENDICION"]?.toLowerCase() || "").includes(
            searchTerm.toLowerCase()
          )
      );
      setFilteredSaldos(filtered);
    }
  }, [saldos, searchTerm]);

  const handleClose = () => {
    props.setopen(false);
    props.tabla && props.tabla(true);
  };

  const noHayNadaParaMostrar = (
    <Card>
      <CardHeader
        avatar={<Avatar sx={{ bgcolor: "red" }}>R</Avatar>}
        title={`No hay datos registrados`}
      />
    </Card>
  );

  const formatFecha = (fecha) => {
    if (!fecha || fecha.trim() === "") return "-";
    const cleanFecha = fecha.replace(/\u0000/g, "").trim();
    if (!cleanFecha) return "-";
    const fechaFormateada = moment(cleanFecha, "YYYYMMDD", true);
    return fechaFormateada.isValid()
      ? fechaFormateada.format("DD/MM/YYYY")
      : cleanFecha;
  };

  const coloresComprobante = {
    "01": "#8497f0",
    60: "#efc3c3",
    17: "#8497f0",
    54: "#efc3c3",
    52: "#efc3c3",
    "03": "#8497f0",
    "02": "#8497f0",
    55: "#efc3c3",
    67: "#efc3c3",
  };

  const getColorByTipoComp = (tipoComp) =>
    coloresComprobante[tipoComp] || "#fff";

  const nombreComprobantes = {
    "01": "FcA",
    60: "Rec",
    17: "NDi",
    54: "NCi",
    52: "NcA",
    "03": "NdB",
    "02": "FcB",
    55: "CrB",
    67: "Rec",
  };

  const getNombreByTipoComp = (tipoComp) => nombreComprobantes[tipoComp] || "-";

  // Mostrar todos los registros recibidos (sin slice, ya que el backend hace la paginación)
  const paginatedSaldos = filteredSaldos;

  return (
    <Dialog open={props.open} fullScreen onClose={handleClose}>
      <Box justifyContent="center" backgroundColor="#1F618D">
        <DialogTitle sx={{ backgroundColor: "#2B76A6", px: 2, py: 1 }}>
          <Grid container alignItems="center">
            <Grid item>
              <IconButton onClick={handleClose} sx={{ color: "#fff" }}>
                <ArrowBackIcon />
              </IconButton>
            </Grid>
            <Grid item xs>
              <Typography fontSize={20} align="center" color="#ffffff">
                Saldo de cliente ({props.dataCliente.sisven_id})
                {props.dataCliente.razon_social ?? props.dataCliente.cliente} -
                Saldo en cta. cte: {total && parseFloat(total).toFixed(2)}
              </Typography>
            </Grid>
          </Grid>
        </DialogTitle>
      </Box>

      <DialogContent>
        {/* Search Input */}
        <Box sx={{ mb: 2, mt: 2 }}>
          <TextField
            label="Buscar..."
            variant="outlined"
            value={searchTerm}
            onChange={handleSearch}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {loading ? (
          <Box sx={{ height: 40, size: 40, marginLeft: "50%" }}>
            <CircularProgress disableShrink />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead sx={{ backgroundColor: "#535e66" }}>
                <TableRow>
                  <TableCell sx={{ color: "white" }}>Fecha Mov</TableCell>
                  <TableCell sx={{ color: "white" }}>Tipo</TableCell>
                  <TableCell sx={{ color: "white" }}>Comprobante</TableCell>
                  <TableCell sx={{ color: "white" }}>Fecha Venc</TableCell>
                  <TableCell sx={{ color: "white" }}>Rendición</TableCell>
                  <TableCell sx={{ color: "white" }}>Total</TableCell>
                  <TableCell sx={{ color: "white" }}>Saldo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedSaldos.length > 0 ? (
                  paginatedSaldos.map((row, index) => (
                    <TableRow
                      key={index}
                      style={{
                        backgroundColor: getColorByTipoComp(
                          row["CCD-TIPO-COMP"]
                        ),
                      }}
                    >
                      <TableCell>{formatFecha(row["CCD-FECHA-MOV"])}</TableCell>
                      <TableCell>
                        {getNombreByTipoComp(row["CCD-TIPO-COMP"])}
                      </TableCell>
                      <TableCell>{row["CCD-COMPROBANTE"]}</TableCell>
                      <TableCell>
                        {formatFecha(row["CCD-FECHA-VENC"])}
                      </TableCell>
                      <TableCell>{row["CCD-RENDICION"]}</TableCell>
                      <TableCell>
                        {parseFloat(row["CCD-TOTAL"]).toFixed(2)}
                      </TableCell>
                      <TableCell>
                        {parseFloat(row["SALDO"]).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <>{noHayNadaParaMostrar}</>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={
            searchTerm.trim() === "" ? totalRegistros : filteredSaldos.length
          } // Ajustar count según búsqueda
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página"
        />
      </DialogContent>
    </Dialog>
  );
}
